import { text } from '@/constants/Styles';
import { colorResolver } from '@/helper/Color';
import { defaultHtmlReplacer } from '@/helper/Html';
import { isJSON } from "@/helper/Json";
import { resolvedBreakpoint } from "@/helper/Resolver";
import { useResponsiveTextStyles } from '@/hooks/useResponsiveTextStyles';
import { useThemeColor } from '@/hooks/useThemeColor';
import { Translate } from '@/utils/Translate';
import parse from 'html-react-parser';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import { createElement, forwardRef, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { Platform, Text as RNText } from 'react-native';

export const CoreText = memo(forwardRef((props, ref) => {
    
    const {
        children,
        style, 
        light, 
        dark, 
        type = 'default', 
        inverted = false, 
        noSwitch = false,
        numberOfLines = null,
        linebreaks = true,
        id = null,
        html = false,
        options = [],
        variant = null,
        append = '',
        prepend = '',
        prependProps = null,
        appendProps = null,
        role = null,
        level = null,
        parserReplace = null,
        title = null,
        translateOptions = false,
        ...rest
    } = props || {};

    const innerRef = useRef(null)

    const setRef = useCallback((node) => {
      innerRef.current = node;

      if (!ref) return;
      if (typeof ref === 'function') {
        ref(node);
      } else {
        ref.current = node;
      }
    }, [ref]);

    const effectiveHoverTitle = title ?? null;

    const themeColor = useThemeColor({ 
        light: inverted ? dark : light, 
        dark: inverted ? light : dark 
    }, 'text');

    const { resolveVariants } = useResponsiveTextStyles();

    const variantStyle = useMemo(() => {
        const coreVariant = variant == null && style == null
            ? "p" : variant;
            
        if (isJSON(coreVariant)) {
            const resolvedVariant = resolvedBreakpoint(coreVariant);
            return resolveVariants(resolvedVariant);
        }

        return resolveVariants(coreVariant);
    }, [variant, resolveVariants, style]);

    const composedStyle = useMemo(() => {
        const collectedStyles = [
			text[type],
			variantStyle,
			style
		];
		
        if (noSwitch) {
            const resolvedColor = style?.color != null ? {color: colorResolver(style.color)} : undefined;
            collectedStyles.push(resolvedColor);
        } else {
            collectedStyles.push({ color: variantStyle?.color || themeColor || style?.color});
        }
		
        const filtered = collectedStyles?.filter(s => s != null && !isEmpty(s));
        return isEmpty(filtered) ? undefined : filtered;
    }, [noSwitch, type, variantStyle, style, themeColor]);
    
    const replace = useCallback((node, item) => {
        if (parserReplace) 
            return parserReplace(node, item);

        return defaultHtmlReplacer(node, item);
    },[parserReplace]);

    const parsedHtml = useMemo(() => 
        html && id != null ? parse(id, {
            trim: false,
            decodeEntities: true,
            replace
        }): null
    , [html, id, replace]);

    const mainContent = id != null
      ? html
            ?   parsedHtml
            :   createElement(Translate, {
                    id,
                    linebreaks,
                    options,
                    translateOptions,
                })
      : children;
    
    useEffect(() => {
      if (Platform.OS !== 'web') return;

      const node = innerRef?.current;
      if (node && typeof node.setAttribute === "function") {
        if (effectiveHoverTitle) {
          node.setAttribute("title", String(effectiveHoverTitle));
        } else {
          node.removeAttribute("title");
        }
      }
    }, [effectiveHoverTitle]);

    return (
      <RNText
        ref={setRef}
        style={composedStyle}
        numberOfLines={numberOfLines}
        accessibilityRole={role}
        aria-level={level}
        {...rest}
      >
        {prepend
          ? typeof prepend === "function"
            ? prepend()
            : (
              <Translate
                id={prepend}
                linebreaks={linebreaks}
                {...(prependProps || {})}
              />
            )
          : null}

        {mainContent}

        {append
          ? typeof append === "function"
            ? append()
            : (
              <Translate
                id={append}
                linebreaks={linebreaks}
                {...(appendProps || {})}
              />
            )
          : null}
      </RNText>
    );

}), isEqual);